// TradingView Market Data API Client
// Connects to the Railway backend for real OHLCV data

import { Bar, SymbolInfo } from './types';

// API base URL from environment or fallback
export const MARKET_DATA_URL = 
  import.meta.env.VITE_MARKET_DATA_URL || 
  'https://dextoro-market-data-production.up.railway.app';

// Request timeout in milliseconds
const REQUEST_TIMEOUT = 20000;

// Shorter timeout for non-critical enrichment endpoints (e.g. price changes)
const ENRICHMENT_TIMEOUT = 5000;

// Retry configuration
const MAX_RETRIES = 3;
const INITIAL_DELAY_MS = 500;

// API Response interfaces
export interface HistoryResponse {
  s: 'ok' | 'no_data' | 'error';
  t?: number[];  // timestamps (Unix seconds)
  o?: number[];  // open prices
  h?: number[];  // high prices
  l?: number[];  // low prices
  c?: number[];  // close prices
  v?: number[];  // volumes
  nextTime?: number;  // Unix seconds: nearest available data when s='no_data'
  errmsg?: string;
}

export interface SymbolInfoResponse {
  name: string;
  ticker: string;
  description: string;
  type: string;
  session: string;
  timezone: string;
  exchange: string;
  minmov: number;
  pricescale: number;
  has_intraday: boolean;
  has_seconds: boolean;
  has_daily: boolean;
  has_weekly_and_monthly: boolean;
  supported_resolutions: string[];
  volume_precision: number;
  data_status: string;
}

export interface SearchResult {
  symbol: string;
  full_name: string;
  description: string;
  exchange: string;
  type: string;
}

// Convert HTTP URL to WebSocket URL
export function getWsUrl(): string {
  const httpUrl = MARKET_DATA_URL;
  const wsUrl = httpUrl.replace(/^https?:\/\//, 'wss://');
  return `${wsUrl}/tv/stream`;
}

// Fetch with timeout
async function fetchWithTimeout(
  url: string,
  options: RequestInit = {},
  timeout: number = REQUEST_TIMEOUT
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(`Request timeout after ${timeout}ms`), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return response;
  } finally {
    clearTimeout(timeoutId);
  }
}

// Check API health
export async function checkApiHealth(): Promise<boolean> {
  try {
    const response = await fetchWithTimeout(`${MARKET_DATA_URL}/health`);
    return response.ok;
  } catch (error) {
    console.warn('[Datafeed] Health check failed:', error);
    return false;
  }
}

/**
 * Sleep for a given duration
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Check if an error is retryable (network issues, server errors, timeouts)
 */
function isRetryableError(error: unknown): boolean {
  if (error instanceof Error) {
    // Abort errors are not retryable (widget teardown, navigation)
    if (error.name === 'AbortError') return false;
    // Network errors
    if (error.message.includes('fetch')) return true;
    // Server errors (5xx)
    if (error.message.includes('HTTP 5')) return true;
    // Rate limiting (429)
    if (error.message.includes('HTTP 429')) return true;
  }
  return false;
}

// Fetch historical bars from API with retry logic
export async function fetchHistory(
  symbol: string,
  resolution: string,
  from: number,
  to: number,
  poolAddress?: string
): Promise<{ bars: Bar[]; noData: boolean; nextTime?: number }> {
  const url = new URL(`${MARKET_DATA_URL}/tv/history`);
  url.searchParams.set('symbol', symbol);
  url.searchParams.set('resolution', resolution);
  url.searchParams.set('from', String(from));
  url.searchParams.set('to', String(to));
  if (poolAddress) {
    url.searchParams.set('pool_address', poolAddress);
  }

  let lastError: Error | null = null;

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      const response = await fetchWithTimeout(url.toString());

      if (!response.ok) {
        const error = new Error(`HTTP ${response.status}: ${response.statusText}`);

        // Retry on server errors or rate limiting
        if (response.status >= 500 || response.status === 429) {
          lastError = error;
          if (attempt < MAX_RETRIES - 1) {
            const delay = INITIAL_DELAY_MS * Math.pow(2, attempt);
            console.warn(`[Datafeed] fetchHistory attempt ${attempt + 1}/${MAX_RETRIES} failed (HTTP ${response.status}), retrying in ${delay}ms`);
            await sleep(delay);
            continue;
          }
        }

        throw error;
      }

      const data: HistoryResponse = await response.json();

      if (data.s === 'error') {
        throw new Error(data.errmsg || 'Unknown API error');
      }

      if (data.s === 'no_data' || !data.t || data.t.length === 0) {
        return { bars: [], noData: true, nextTime: data.nextTime };
      }

      // Convert API response to TradingView bars
      // API returns Unix seconds, TradingView expects milliseconds
      const bars: Bar[] = data.t.map((time, i) => ({
        time: time * 1000,
        open: data.o![i],
        high: data.h![i],
        low: data.l![i],
        close: data.c![i],
        volume: data.v?.[i] ?? 0,
      }));

      return { bars, noData: false };
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // Only retry on retryable errors
      if (isRetryableError(error) && attempt < MAX_RETRIES - 1) {
        const delay = INITIAL_DELAY_MS * Math.pow(2, attempt);
        console.warn(`[Datafeed] fetchHistory attempt ${attempt + 1}/${MAX_RETRIES} failed, retrying in ${delay}ms:`, error);
        await sleep(delay);
        continue;
      }

      throw error;
    }
  }

  // All retries exhausted
  throw lastError || new Error('All retry attempts failed');
}

// Fetch symbol info from API
export async function fetchSymbolInfo(symbol: string): Promise<SymbolInfo | null> {
  try {
    const url = new URL(`${MARKET_DATA_URL}/tv/symbols`);
    url.searchParams.set('symbol', symbol);

    const response = await fetchWithTimeout(url.toString());
    
    if (!response.ok) {
      return null;
    }

    const data: SymbolInfoResponse = await response.json();
    return data as SymbolInfo;
  } catch (error) {
    console.warn('[Datafeed] Failed to fetch symbol info:', error);
    return null;
  }
}

// Search symbols from API
export async function searchSymbols(query: string): Promise<SearchResult[]> {
  try {
    const url = new URL(`${MARKET_DATA_URL}/tv/search`);
    url.searchParams.set('query', query);

    const response = await fetchWithTimeout(url.toString());

    if (!response.ok) {
      return [];
    }

    return await response.json();
  } catch (error) {
    console.warn('[Datafeed] Symbol search failed:', error);
    return [];
  }
}

// Fetch current bar for a symbol/resolution (REST polling fallback)
export async function fetchCurrentBar(
  symbol: string,
  resolution: string
): Promise<Bar | null> {
  try {
    const url = new URL(`${MARKET_DATA_URL}/tv/current`);
    url.searchParams.set('symbol', symbol);
    url.searchParams.set('resolution', resolution);

    const response = await fetchWithTimeout(url.toString());

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    if (!data || !data.time) return null;

    return {
      time: data.time * 1000, // API returns Unix seconds
      open: data.open,
      high: data.high,
      low: data.low,
      close: data.close,
      volume: data.volume ?? 0,
    };
  } catch (error) {
    console.warn('[Datafeed] fetchCurrentBar failed:', error);
    return null;
  }
}

// Pool snapshot response type (prices + reserves + all price changes in one call)
export interface PoolSnapshotEntry {
  price: number;
  quoteReserve: number | null;
  priceChange5m: number | null;
  priceChange1h: number | null;
  priceChange6h: number | null;
  priceChange24h: number | null;
}

export interface PoolSnapshotData {
  [poolAddress: string]: PoolSnapshotEntry;
}

// Dedup cache for fetchPoolSnapshot
let _poolSnapshotPromise: Promise<PoolSnapshotData> | null = null;
let _poolSnapshotTimestamp = 0;
const POOL_SNAPSHOT_CACHE_TTL = 10_000; // 10s, matches backend cache TTL

// Fetch pool snapshot for all pools (with retry + dedup)
export async function fetchPoolSnapshot(): Promise<PoolSnapshotData> {
  const now = Date.now();

  // Return in-flight or recently-resolved promise if within TTL
  if (_poolSnapshotPromise && now - _poolSnapshotTimestamp < POOL_SNAPSHOT_CACHE_TTL) {
    return _poolSnapshotPromise;
  }

  _poolSnapshotTimestamp = now;
  _poolSnapshotPromise = _fetchPoolSnapshotWithRetry();

  // Clear cache on failure so next caller retries
  _poolSnapshotPromise.catch(() => {
    _poolSnapshotPromise = null;
  });

  return _poolSnapshotPromise;
}

/**
 * Clear the client-side pool snapshot cache.
 * Call after creating a new token so the next fetch picks up the new pool.
 */
export function clearPoolSnapshotCache(): void {
  _poolSnapshotPromise = null;
  _poolSnapshotTimestamp = 0;
}

async function _fetchPoolSnapshotWithRetry(): Promise<PoolSnapshotData> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      const response = await fetchWithTimeout(
        `${MARKET_DATA_URL}/tv/pool-snapshot`,
        {},
        ENRICHMENT_TIMEOUT
      );

      if (!response.ok) {
        const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
        if ((response.status >= 500 || response.status === 429) && attempt < MAX_RETRIES - 1) {
          lastError = error;
          const delay = INITIAL_DELAY_MS * Math.pow(2, attempt);
          console.warn(`[Datafeed] fetchPoolSnapshot attempt ${attempt + 1}/${MAX_RETRIES} failed (HTTP ${response.status}), retrying in ${delay}ms`);
          await sleep(delay);
          continue;
        }
        console.warn('[Datafeed] Pool snapshot failed:', error);
        return {};
      }

      const json = await response.json();
      return json.data ?? {};
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      if (isRetryableError(error) && attempt < MAX_RETRIES - 1) {
        const delay = INITIAL_DELAY_MS * Math.pow(2, attempt);
        console.warn(`[Datafeed] fetchPoolSnapshot attempt ${attempt + 1}/${MAX_RETRIES} failed, retrying in ${delay}ms:`, error);
        await sleep(delay);
        continue;
      }

      console.warn('[Datafeed] Pool snapshot failed:', error);
      return {};
    }
  }

  console.warn('[Datafeed] Pool snapshot exhausted retries:', lastError);
  return {};
}

// Batch price changes response type
export interface BatchPriceChanges {
  [poolAddress: string]: {
    change5m: number | null;
    change1h: number | null;
    change6h: number | null;
  };
}

// Dedup cache for fetchBatchPriceChanges — concurrent callers share one HTTP request
let _priceChangesPromise: Promise<BatchPriceChanges> | null = null;
let _priceChangesTimestamp = 0;
const PRICE_CHANGES_CACHE_TTL = 15_000; // 15s, matches useBatchPriceChanges staleTime

// Fetch batch price changes for all pools (with retry + dedup)
export async function fetchBatchPriceChanges(): Promise<BatchPriceChanges> {
  const now = Date.now();

  // Return in-flight or recently-resolved promise if within TTL
  if (_priceChangesPromise && now - _priceChangesTimestamp < PRICE_CHANGES_CACHE_TTL) {
    return _priceChangesPromise;
  }

  _priceChangesTimestamp = now;
  _priceChangesPromise = _fetchBatchPriceChangesWithRetry();

  // Clear cache on failure so next caller retries
  _priceChangesPromise.catch(() => {
    _priceChangesPromise = null;
  });

  return _priceChangesPromise;
}

async function _fetchBatchPriceChangesWithRetry(): Promise<BatchPriceChanges> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      const response = await fetchWithTimeout(
        `${MARKET_DATA_URL}/tv/price-changes`,
        {},
        ENRICHMENT_TIMEOUT
      );

      if (!response.ok) {
        const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
        if ((response.status >= 500 || response.status === 429) && attempt < MAX_RETRIES - 1) {
          lastError = error;
          const delay = INITIAL_DELAY_MS * Math.pow(2, attempt);
          console.warn(`[Datafeed] fetchBatchPriceChanges attempt ${attempt + 1}/${MAX_RETRIES} failed (HTTP ${response.status}), retrying in ${delay}ms`);
          await sleep(delay);
          continue;
        }
        console.warn('[Datafeed] Batch price changes failed:', error);
        return {};
      }

      const json = await response.json();
      return json.data ?? {};
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      if (isRetryableError(error) && attempt < MAX_RETRIES - 1) {
        const delay = INITIAL_DELAY_MS * Math.pow(2, attempt);
        console.warn(`[Datafeed] fetchBatchPriceChanges attempt ${attempt + 1}/${MAX_RETRIES} failed, retrying in ${delay}ms:`, error);
        await sleep(delay);
        continue;
      }

      console.warn('[Datafeed] Batch price changes failed:', error);
      return {};
    }
  }

  console.warn('[Datafeed] Batch price changes exhausted retries:', lastError);
  return {};
}
